/**
 * Restores HTML that was modified by safeHtmlParser back to its original structure.
 * This function reverses all transformations made to handle browser parsing quirks,
 * converting elements back to their original tags and removing marker attributes.
 */

// Marker attributes used by safeHtmlParser that need to be restored
const REPLACEMENT_MARKERS = {
  NESTED_BUTTON: "data-safe-parser-nested-button",
  NESTED_FORM: "data-safe-parser-nested-form",
  NESTED_ANCHOR: "data-safe-parser-nested-anchor",
  INVALID_TABLE: "data-safe-parser-invalid-table",
  INVALID_OPTION: "data-safe-parser-invalid-option",
  BLOCK_IN_P: "data-safe-parser-block-in-p",
  INVALID_HEAD_ELEMENT: "data-safe-parser-invalid-head-element",
  BUTTON_IN_HEADER: "data-safe-parser-button-in-header",
  NESTED_HEADER: "data-safe-parser-nested-header",
} as const;

/**
 * Restores a parsed Document back to its original HTML structure in place
 * @param doc - The Document that was parsed with safeHtmlParser (modified in place)
 */
export function restoreOriginalDocument(doc: Document): void {
  // Restore all transformed elements directly on the document
  restoreNestedHeaders(doc);
  restoreInteractiveElementsInTextTags(doc);
  restoreNestedButtons(doc);
  restoreNestedForms(doc);
  restoreNestedAnchors(doc);
  restoreInvalidTableElements(doc);
  restoreInvalidOptions(doc);
  removeBlockInParagraphMarkers(doc);

  // Restore invalid head elements and HTML entities
  restoreInvalidHeadElementsFromComments(doc);
}

/**
 * Restores nested headers that were converted to spans
 */
function restoreNestedHeaders(doc: Document): void {
  const convertedHeaders = doc.querySelectorAll(
    `[${REPLACEMENT_MARKERS.NESTED_HEADER}]`,
  );

  convertedHeaders.forEach((span) => {
    const originalTag = span.getAttribute(REPLACEMENT_MARKERS.NESTED_HEADER);
    if (!originalTag) return;

    const headerElement = doc.createElement(originalTag);

    // Copy all attributes except the marker
    Array.from(span.attributes ?? []).forEach((attr) => {
      if (attr.name !== REPLACEMENT_MARKERS.NESTED_HEADER) {
        headerElement.setAttribute(attr.name, attr.value);
      }
    });

    // Move all children to the new element
    while (span.firstChild) {
      headerElement.appendChild(span.firstChild);
    }

    // Replace the span with the header element
    span.parentNode?.replaceChild(headerElement, span);
  });
}

/**
 * Restores interactive elements that were converted to spans inside text tags
 */
function restoreInteractiveElementsInTextTags(doc: Document): void {
  const convertedElements = doc.querySelectorAll(
    `[${REPLACEMENT_MARKERS.BUTTON_IN_HEADER}]`,
  );

  convertedElements.forEach((span) => {
    const originalTag = span.getAttribute(REPLACEMENT_MARKERS.BUTTON_IN_HEADER);
    if (!originalTag) return;

    const interactiveElement = doc.createElement(originalTag);

    // Copy all attributes except the marker
    Array.from(span.attributes ?? []).forEach((attr) => {
      if (attr.name !== REPLACEMENT_MARKERS.BUTTON_IN_HEADER) {
        interactiveElement.setAttribute(attr.name, attr.value);
      }
    });

    // Move all children to the new element
    while (span.firstChild) {
      interactiveElement.appendChild(span.firstChild);
    }

    // Replace the span with the interactive element
    span.parentNode?.replaceChild(interactiveElement, span);
  });
}

/**
 * Restores nested buttons that were converted to divs
 */
function restoreNestedButtons(doc: Document): void {
  const convertedButtons = doc.querySelectorAll(
    `[${REPLACEMENT_MARKERS.NESTED_BUTTON}="true"]`,
  );

  convertedButtons.forEach((div) => {
    const button = doc.createElement("button");

    // Copy all attributes except the marker
    Array.from(div.attributes ?? []).forEach((attr) => {
      if (attr.name !== REPLACEMENT_MARKERS.NESTED_BUTTON) {
        button.setAttribute(attr.name, attr.value);
      }
    });

    // Move all children to the new button
    while (div.firstChild) {
      button.appendChild(div.firstChild);
    }

    // Replace the div with the button
    div.parentNode?.replaceChild(button, div);
  });
}

/**
 * Restores nested forms that were converted to divs
 */
function restoreNestedForms(doc: Document): void {
  const convertedForms = doc.querySelectorAll(
    `[${REPLACEMENT_MARKERS.NESTED_FORM}="true"]`,
  );

  convertedForms.forEach((div) => {
    const form = doc.createElement("form");

    // Copy all attributes except the marker
    Array.from(div.attributes ?? []).forEach((attr) => {
      if (attr.name !== REPLACEMENT_MARKERS.NESTED_FORM) {
        form.setAttribute(attr.name, attr.value);
      }
    });

    // Move all children to the new form
    while (div.firstChild) {
      form.appendChild(div.firstChild);
    }

    // Replace the div with the form
    div.parentNode?.replaceChild(form, div);
  });
}

/**
 * Restores nested anchors that were converted to spans
 */
function restoreNestedAnchors(doc: Document): void {
  const convertedAnchors = doc.querySelectorAll(
    `[${REPLACEMENT_MARKERS.NESTED_ANCHOR}="true"]`,
  );

  convertedAnchors.forEach((span) => {
    const anchor = doc.createElement("a");

    // Copy all attributes except the marker
    Array.from(span.attributes ?? []).forEach((attr) => {
      if (attr.name !== REPLACEMENT_MARKERS.NESTED_ANCHOR) {
        anchor.setAttribute(attr.name, attr.value);
      }
    });

    // Move all children to the new anchor
    while (span.firstChild) {
      anchor.appendChild(span.firstChild);
    }

    // Replace the span with the anchor
    span.parentNode?.replaceChild(anchor, span);
  });
}

/**
 * Restores table elements (tr, td, th) that were converted to divs
 */
function restoreInvalidTableElements(doc: Document): void {
  // We need to process in reverse order (innermost first) to handle nested conversions
  // Get all converted elements and sort by depth in the DOM tree
  const convertedTableElements = Array.from(
    doc.querySelectorAll(`[${REPLACEMENT_MARKERS.INVALID_TABLE}]`),
  );

  // Sort by depth (deepest first) to restore from inside out
  convertedTableElements.sort((a, b) => {
    const depthA = getElementDepth(a);
    const depthB = getElementDepth(b);
    return depthB - depthA;
  });

  convertedTableElements.forEach((div) => {
    const originalTag = div.getAttribute(REPLACEMENT_MARKERS.INVALID_TABLE);
    if (!originalTag) return;

    const tableElement = doc.createElement(originalTag);

    // Copy all attributes except the marker
    Array.from(div.attributes ?? []).forEach((attr) => {
      if (attr.name !== REPLACEMENT_MARKERS.INVALID_TABLE) {
        tableElement.setAttribute(attr.name, attr.value);
      }
    });

    // Move all children to the new element
    while (div.firstChild) {
      tableElement.appendChild(div.firstChild);
    }

    // Replace the div with the table element
    div.parentNode?.replaceChild(tableElement, div);
  });
}

/**
 * Helper function to get the depth of an element in the DOM tree
 */
function getElementDepth(element: Element): number {
  let depth = 0;
  let current = element.parentElement;
  while (current) {
    depth++;
    current = current.parentElement;
  }
  return depth;
}

/**
 * Restores option elements that were converted to divs
 */
function restoreInvalidOptions(doc: Document): void {
  const convertedOptions = doc.querySelectorAll(
    `[${REPLACEMENT_MARKERS.INVALID_OPTION}="true"]`,
  );

  convertedOptions.forEach((div) => {
    const option = doc.createElement("option");

    // Copy all attributes except the marker
    Array.from(div.attributes ?? []).forEach((attr) => {
      if (attr.name !== REPLACEMENT_MARKERS.INVALID_OPTION) {
        option.setAttribute(attr.name, attr.value);
      }
    });

    // Move all children to the new option
    while (div.firstChild) {
      option.appendChild(div.firstChild);
    }

    // Replace the div with the option
    div.parentNode?.replaceChild(option, div);
  });
}

/**
 * Restores block elements that were converted to spans inside paragraphs
 */
function removeBlockInParagraphMarkers(doc: Document): void {
  const convertedBlocks = doc.querySelectorAll(
    `[${REPLACEMENT_MARKERS.BLOCK_IN_P}]`,
  );

  convertedBlocks.forEach((span) => {
    const originalTag = span.getAttribute(REPLACEMENT_MARKERS.BLOCK_IN_P);
    if (!originalTag) return;

    const blockElement = doc.createElement(originalTag);

    // Copy all attributes except the marker
    Array.from(span.attributes ?? []).forEach((attr) => {
      if (attr.name !== REPLACEMENT_MARKERS.BLOCK_IN_P) {
        blockElement.setAttribute(attr.name, attr.value);
      }
    });

    // Move all children to the new element
    while (span.firstChild) {
      blockElement.appendChild(span.firstChild);
    }

    // Replace the span with the block element
    span.parentNode?.replaceChild(blockElement, span);
  });
}

/**
 * Restores invalid head elements that were encoded in script tags
 */
export function restoreInvalidHeadElementsFromComments(doc: Document): void {
  // Find the storage script that contains encoded elements
  const storageScript = doc.querySelector(
    'script[type="text/x-safe-parser-storage"]',
  );
  if (!storageScript) return;

  let invalidElements: string[];
  try {
    invalidElements = JSON.parse(storageScript.textContent || "[]");
  } catch (e) {
    return;
  }

  // Find all placeholder scripts and replace them
  const placeholderScripts = doc.querySelectorAll(
    'script[type="text/x-safe-parser-invalid-head"]',
  );
  placeholderScripts.forEach((script) => {
    const index = parseInt(
      script.getAttribute("data-element-index") || "-1",
      10,
    );
    if (index >= 0 && index < invalidElements.length) {
      // Decode the base64 element and parse it
      const elementHtml = atob(invalidElements[index]);
      const tempDiv = doc.createElement("div");
      tempDiv.innerHTML = elementHtml;

      // Replace the script with the decoded element
      if (tempDiv.firstChild) {
        script.parentNode?.replaceChild(tempDiv.firstChild, script);
      }
    }
  });

  // Remove the storage script
  storageScript.remove();
}
